import {ERequestType, TRequestTableResponseWithPagination} from 'shared/types/requests';
import {IRpcDeleteResponse} from 'shared/types/rpc';
import {performRequest} from 'shared/utils';

import {FLEX_FIELDS_API_PREFIX} from './flex-fields-slice-constants';
import {
    ICreateFlexFieldsContextDto,
    ICreateFlexFieldsSegmentDto,
    IFlexFieldRecordDto,
    IFlexFieldsSegmentsTableDto,
    IFlexFieldsSettingsTableDto,
    IUpdateFlexFieldRecordDto,
    IUpdateFlexFieldSegmentDto,
} from './flex-fields-slice-types';

// ==== API to work with Flex Field Settings Table ====
export const fetchFlexFieldsSettingsTable = (
    {
        dffName,
        paginationPageSize,
    }: IFetchFlexFieldsSettingsTableParams,
) => performRequest<TRequestTableResponseWithPagination<IFlexFieldsSettingsTableDto>>({
    url: `${FLEX_FIELDS_API_PREFIX}/${dffName}/table`,
    method: ERequestType.GET,
    params: {
        paginationPageSize,
    },
});

export const deleteFlexFieldsSettingsTableRowsRequest = async (
    {
        dffName,
        ids,
    }: IDeleteFlexFieldsSettingsTableRowsParams,
) => performRequest<IRpcDeleteResponse>({
    url: `${FLEX_FIELDS_API_PREFIX}/${dffName}/delete`,
    method: ERequestType.DELETE,
    data: {ids},
});

export const createFlexFieldsSettingsTableRowsRequest = async (
    {
        dffName,
        ids,
    }: IDeleteFlexFieldsSettingsTableRowsParams,
) => performRequest<string>({
    url: `${FLEX_FIELDS_API_PREFIX}/${dffName}/delete`,
    method: ERequestType.DELETE,
    data: {ids},
});

// ==== API to work with Flex Field Segments Table ====
export const fetchFlexFieldsSegmentsTable = (
    {
        dffName,
        dffCode,
        paginationPageSize,
    }: IFetchFlexFieldsSegmentsTableParams,
) => performRequest<TRequestTableResponseWithPagination<IFlexFieldsSegmentsTableDto>>({
    url: `${FLEX_FIELDS_API_PREFIX}/${dffName}/${dffCode}/table`,
    method: ERequestType.GET,
    params: {
        paginationPageSize,
    },
});

export const deleteFlexFieldsSegmentsTableRowsRequest = async (
    {
        dffName,
        dffCode,
        ids,
    }: IDeleteFlexFieldsSegmentsTableRowsParams,
) => performRequest<IRpcDeleteResponse>({
    url: `${FLEX_FIELDS_API_PREFIX}/${dffName}/${dffCode}/delete`,
    method: ERequestType.DELETE,
    data: {ids},
});

// ==== API to work with Flex Field Record Object Form ====
export const fetchFlexFieldsRecord = (
    {
        dffName,
    }: IFetchFlexFieldRecordParams,
) => performRequest<IFlexFieldRecordDto>({
    url: `${FLEX_FIELDS_API_PREFIX}/${dffName}/`,
    method: ERequestType.GET,
});

export const putFlexFieldsRecord = async (
    {
        dffName,
        record,
    }: IUpdateFlexFieldRecordParams,
) => performRequest<string>({
    url: `${FLEX_FIELDS_API_PREFIX}/${dffName}/`,
    method: ERequestType.PUT,
    data: record,
});

// ==== API to work with Flex Field Context Form ====
export const postFlexFieldsContext = async (
    {
        dffName,
        context,
    }: IPostFlexFieldsContextParams,
) => performRequest<string>({
    url: `${FLEX_FIELDS_API_PREFIX}/${dffName}/create`,
    method: ERequestType.POST,
    data: context,
});

export const putFlexFieldsContext = async (
    {
        dffName,
        context,
    }: IUpdateFlexFieldsContextParams,
) => performRequest<string>({
    url: `${FLEX_FIELDS_API_PREFIX}/${dffName}/update`,
    method: ERequestType.PUT,
    data: context,
});

// ==== API to work with Flex Field Segment Form ====
export const postFlexFieldsSegment = async (
    {
        dffName,
        dffCode,
        segment,
    }: IPostFlexFieldsSegmentParams,
) => performRequest<string>({
    url: `${FLEX_FIELDS_API_PREFIX}/${dffName}/${dffCode}/create`,
    method: ERequestType.POST,
    data: segment,
});

export const putFlexFieldsSegment = async (
    {
        dffName,
        dffCode,
        segment,
    }: IUpdateFlexFieldsSegmentParams,
) => performRequest<string>({
    url: `${FLEX_FIELDS_API_PREFIX}/${dffName}/${dffCode}/update`,
    method: ERequestType.PUT,
    data: segment,
});

// ==== Params types for requests ====
export interface IFetchFlexFieldRecordParams {
    dffName: string;
}

export interface IFetchFlexFieldSegmentParams extends IFetchFlexFieldRecordParams {
    dffCode: string;
}

export interface IPaginationPageSize {
    paginationPageSize: number;
}

export interface IUpdateFlexFieldRecordParams extends IFetchFlexFieldRecordParams {
    record: IUpdateFlexFieldRecordDto;
}

export interface IFetchFlexFieldsSettingsTableParams extends IFetchFlexFieldRecordParams, IPaginationPageSize {}

export interface IDeleteFlexFieldsSettingsTableRowsParams extends IFetchFlexFieldRecordParams {
    ids: number[];
}

export interface IFetchFlexFieldsSegmentsTableParams extends IFetchFlexFieldSegmentParams, IPaginationPageSize {}

export interface IDeleteFlexFieldsSegmentsTableRowsParams extends IFetchFlexFieldSegmentParams {
    ids: number[];
}

export interface IPostFlexFieldsContextParams extends IFetchFlexFieldRecordParams {
    context: ICreateFlexFieldsContextDto;
}

export interface IUpdateFlexFieldsContextParams extends IFetchFlexFieldRecordParams {
    context: IFlexFieldsSettingsTableDto;
}

export interface IPostFlexFieldsSegmentParams extends IFetchFlexFieldSegmentParams {
    segment: ICreateFlexFieldsSegmentDto;
}

export interface IUpdateFlexFieldsSegmentParams extends IFetchFlexFieldSegmentParams {
    segment: IUpdateFlexFieldSegmentDto;
}
